import { useCallback, useEffect, useMemo, useState } from "react";

const INITIAL_SIZE_PER_PAGE = 25
const INITIAL_SORT_FIELD = 'id'
const INITIAL_SORT_ORDER_DESC = false

function useStateOptions([sorting]) {
  const [, setPage] = useState(1)
  const [sizePerPage, setSizePerPage] = useState(INITIAL_SIZE_PER_PAGE)

  const { sortField, sortOrder } = useMemo(() => {
    const { id: sortField, desc } = sorting || {}
    const sortOrder = {
      true: 'desc',
      false: 'asc',
    }[desc]

    return {
      sortField, sortOrder,
    }
  }, [sorting])

  const nextPage = useCallback(() => {
    setPage(page => {
      page = page + 1
      setSizePerPage(INITIAL_SIZE_PER_PAGE * 2 ** (page - 1))
      return page
    })
  }, [])

  return useMemo(() => ({
    page: 1,
    setPage,
    sizePerPage, setSizePerPage,
    sortField, sortOrder,
    nextPage,
  }), [sizePerPage, sortField, sortOrder, nextPage])
}

function useStateFilters(definitionFilters) {
  const [object, setObject] = useState({})
  const [string, setString] = useState('')

  useEffect(() => {
    if (definitionFilters) {
      setObject(definitionFilters)
    }
  }, [definitionFilters])

  useEffect(() => {
    const strings = []
    Object.entries(object).forEach(([key, value]) => {
      strings.push(`${key}=${JSON.stringify(value)}`)
    })

    setString(strings.join(' '))
  }, [object])

  return useMemo(() => ({
    filters: string,
    setFilters: setObject,
    getFilter: key => object[key],
  }), [object, string])
}

export function useStateGrid({ sort: definitionSort, filters: definitionFilters, autoRefresh: definitionAutoRefresh }) {
  const { filters, setFilters, getFilter } = useStateFilters(definitionFilters)
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: INITIAL_SORT_FIELD, desc: INITIAL_SORT_ORDER_DESC }]);
  const [autoRefresh, setAutoRefresh] = useState(false)

  const options = useStateOptions(sorting)

  useEffect(() => {
    if (definitionSort) {
      setSorting(definitionSort)
    }
  }, [definitionSort])

  useEffect(() => {
    if (definitionAutoRefresh) {
      setAutoRefresh(definitionAutoRefresh)
    }
  }, [definitionAutoRefresh])

  useEffect(() => {
    setTerm(() => {
      const terms = []
      if (search) {
        terms.push(search)
      }

      if (filters) {
        terms.push(filters)
      }
      return terms.join(' ')
    })
  }, [search, filters])

  return useMemo(() => ({
    sorting, setSorting,
    filters, setFilters,
    search, setSearch,
    term, setTerm,
    autoRefresh, setAutoRefresh,
    options,
    getFilter,
  }), [sorting, filters, search, term, options, setFilters, getFilter, autoRefresh, setAutoRefresh])
}
